import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cpu, Gamepad2, Globe, ListChecks } from "lucide-react";
import { PageShell, GuideHero } from "@/components/layout/PageShell";
import { BrowserCheck } from "@/components/compat/BrowserCheck";
import { BrowserTable, NotSupported, SupportedCard, Tip } from "@/components/compat/CompatParts";
import {
  BROWSERS, BROWSERS_NOT, BROWSERS_TIP, CONTROLLERS, CONTROLLERS_NOT, CONTROLLERS_TIP, HUBS, HUBS_NOT, HUBS_TIP,
} from "@/components/compat/compatData";

const SECTIONS = [
  { id: "hubs", label: "LEGO hubs", icon: Cpu },
  { id: "controllers", label: "Controllers", icon: Gamepad2 },
  { id: "browsers", label: "Browsers", icon: Globe },
];

const Section = ({ id, title, intro, children }) => (
  <section id={id} className="scroll-mt-24 border-t pt-12" data-testid={`compat-section-${id}`}>
    <h2 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h2>
    <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">{intro}</p>
    <div className="mt-6 space-y-4">{children}</div>
  </section>
);

const Jump = () => (
  <nav className="flex flex-wrap gap-2" aria-label="On this page">
    {SECTIONS.map(({ id, label, icon: Icon }) => (
      <a key={id} href={`#${id}`} className="flex items-center gap-2 rounded-full border bg-card px-4 py-2 text-sm font-semibold transition-colors hover:border-primary hover:text-primary" data-testid={`compat-jump-${id}`}>
        <Icon className="h-4 w-4" />{label}
      </a>
    ))}
  </nav>
);

export default function Compatibility() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <PageShell testId="compatibility-page">
      <GuideHero eyebrow="Compatibility" title="What works with HubPilot" icon={ListChecks}>
        The LEGO hubs, Xbox controllers and browsers you can use, plus anything you need to do first.
      </GuideHero>

      <div className="mx-auto max-w-5xl space-y-12 px-4 py-12 sm:px-8 lg:py-16">
        <div className="space-y-6">
          <BrowserCheck />
          <Jump />
        </div>

        <Section id="hubs" title="LEGO hubs" intro="Every supported hub needs the free Pybricks firmware installed once over USB from code.pybricks.com. It takes about five minutes, and you can switch back to LEGO's firmware whenever you like.">
          <div className="grid gap-4 md:grid-cols-3">
            {HUBS.map((h) => <SupportedCard key={h.id} item={h} testId={`hub-card-${h.id}`} />)}
          </div>
          <Tip testId="hubs-tip">{HUBS_TIP} <Link to="/firmware" className="font-semibold underline" data-testid="compat-firmware-link">Firmware setup guide</Link></Tip>
          <NotSupported items={HUBS_NOT} testId="hubs-not-supported" />
        </Section>

        <Section id="controllers" title="Xbox controllers" intro="The hub pairs straight to genuine Xbox wireless controllers that speak Bluetooth LE. Older ones may need a free update first.">
          <div className="grid gap-4 md:grid-cols-3">
            {CONTROLLERS.map((c) => <SupportedCard key={c.id} item={c} testId={`controller-card-${c.id}`} />)}
          </div>
          <Tip testId="controllers-tip">{CONTROLLERS_TIP} <Link to="/classroom" className="font-semibold underline" data-testid="compat-classroom-link">Classroom pairing guide</Link></Tip>
          <NotSupported items={CONTROLLERS_NOT} testId="controllers-not-supported" />
        </Section>

        <Section id="browsers" title="Browsers" intro="Any browser can set up and save robots. Connecting to a hub uses Web Bluetooth, and installing firmware uses WebUSB. Only some browsers have those.">
          <BrowserTable rows={BROWSERS} />
          <Tip testId="browsers-tip">{BROWSERS_TIP}</Tip>
          <NotSupported items={BROWSERS_NOT} testId="browsers-not-supported" />
        </Section>
      </div>
    </PageShell>
  );
}
